import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { Recruit } from "../lib/api";
import { toast } from "sonner";
import { Upload, FileText, X, Sparkles, Wand2, Check } from "lucide-react";

export default function NewProject() {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [role, setRole] = useState("");
  const [jdText, setJdText] = useState("");
  const [must, setMust] = useState("");
  const [nice, setNice] = useState("");
  const [minYears, setMinYears] = useState("");
  const [industry, setIndustry] = useState("");
  const [notes, setNotes] = useState("");
  const [shortlist, setShortlist] = useState(5);
  const [resumes, setResumes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [drag, setDrag] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const fileRef = useRef();

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (resumes.length + files.length > 50) {
      toast.error("Maximum 50 resumes are allowed per project.");
      setResumes((p) => [...p, ...files].slice(0, 50));
    } else {
      setResumes((p) => [...p, ...files]);
    }
  };

  const handleJDFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await Recruit.parseJDFile(file);
      setJdText(res.jd_text);
      toast.success(`JD loaded: ${file.name}`);
      // Auto-trigger extraction
      setTimeout(() => doExtract(res.jd_text), 200);
    } catch (err) {
      toast.error("Couldn't parse file: " + (err?.response?.data?.detail || err.message));
    }
  };

  const doExtract = async (textOverride) => {
    const text = textOverride ?? jdText;
    if (!text.trim() || text.trim().length < 50) {
      toast.error("Add a job description first (at least a paragraph).");
      return;
    }
    setExtracting(true);
    setExtracted(false);
    try {
      const res = await Recruit.jdExtract(text);
      const x = res.extracted || {};
      if (x.title && !title) setTitle(x.title);
      if (x.role && !role) setRole(x.role);
      if (x.must_have_skills?.length) setMust(x.must_have_skills.join(", "));
      if (x.nice_to_have_skills?.length) setNice(x.nice_to_have_skills.join(", "));
      if (typeof x.min_years === "number" && !minYears) setMinYears(String(x.min_years));
      if (x.industry && !industry) setIndustry(x.industry);
      setExtracted(true);
      toast.success("Auto-filled from JD" + (res.mode === "trial" ? " (free trial)" : ""));
    } catch (err) {
      toast.error("Extract failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Project title is required");
    if (!jdText.trim()) return toast.error("Add a job description (paste or upload)");
    if (resumes.length === 0) return toast.error("Add at least one resume");
    if (resumes.length > 50) return toast.error("Maximum 50 resumes are allowed per project.");
    setBusy(true);
    try {
      const proj = await Recruit.createProject({
        title: title.trim(),
        role: role.trim(),
        jd_text: jdText.trim(),
        context: {
          must_have_skills: must.split(",").map(s => s.trim()).filter(Boolean),
          nice_to_have_skills: nice.split(",").map(s => s.trim()).filter(Boolean),
          min_years: minYears ? Number(minYears) : null,
          industry: industry.trim(),
          recruiter_notes: notes.trim(),
          shortlist_count: Number(shortlist) || 5,
        },
      });
      await Recruit.uploadResumes(proj.id, resumes, (prog) => {
        setUploadProgress(prog);
      });
      toast.success("Project created — let's analyze.");
      nav(`/app/projects/${proj.id}`);
    } catch (e) {
      toast.error("Failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
      setUploadProgress(null);
    }
  };

  const removeResume = (i) => setResumes((p) => p.filter((_, idx) => idx !== i));

  return (
    <div className="min-h-screen bg-canvas" data-testid="new-project-page">
      <Nav />
      <main className="max-w-[1100px] mx-auto px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="eyebrow mb-3">New Hire</div>
          <h1 className="display-1 text-4xl lg:text-5xl text-ink">Brief the Engine</h1>
          <p className="text-sub mt-3 max-w-2xl leading-relaxed">
            Upload a job description, drop resumes, and our metadata extraction will configure the parameters. You can refine the skills, years, and notes before analyzing.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT: JD and Details */}
          <section className="lg:col-span-7 space-y-8">
            {/* Job Description Upload */}
            <div className="card p-8 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-ink">Job Description</h2>
                <div>
                  <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={handleJDFileSelect} className="hidden" data-testid="jd-file-input" />
                  <button 
                    data-testid="jd-upload-btn" 
                    onClick={() => fileRef.current?.click()} 
                    className="btn-ghost py-1.5 px-4 text-xs inline-flex items-center gap-1.5"
                  >
                    <Upload size={12} /> Upload File
                  </button>
                </div>
              </div>
              <textarea
                data-testid="jd-text-input" 
                value={jdText} 
                onChange={(e) => { setJdText(e.target.value); setExtracted(false); }}
                rows={9} 
                placeholder="Paste Job Description requirements here…"
                className="w-full textarea-apple text-xs"
              />
              <div className="flex items-center justify-between mt-3">
                <div className="text-[10px] font-mono text-soft">{jdText.length} characters</div>
                <button
                  onClick={() => doExtract()} 
                  disabled={extracting || jdText.trim().length < 50}
                  data-testid="extract-jd-btn"
                  className="btn-primary py-2 px-4 text-xs disabled:opacity-40 flex items-center gap-2"
                >
                  {extracting ? "Extracting..." : extracted ? (<><Check size={12} /> Auto-filled</>) : (<><Wand2 size={12} /> Auto-fill from JD</>)}
                </button>
              </div>
            </div>

            {/* Context Fields */}
            <div className="card p-8 bg-white space-y-6">
              <h2 className="text-lg font-semibold text-ink">Project Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Project Title <span className="text-accent">*</span></label>
                  <input data-testid="title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Founding Engineer — Q2" className="mt-1.5 block w-full input-apple text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Role / position title</label>
                  <input data-testid="role-input" value={role} onChange={(e) => setRole(e.target.value)}
                    placeholder="Senior Backend Engineer" className="mt-1.5 block w-full input-apple text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Industry / Domain</label>
                  <input data-testid="industry-input" value={industry} onChange={(e) => setIndustry(e.target.value)}
                    placeholder="Fintech, B2B SaaS…" className="mt-1.5 block w-full input-apple text-xs" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Must-have skills <span className="text-soft text-[10px] ml-1">(comma separated)</span></label>
                  <input data-testid="must-input" value={must} onChange={(e) => setMust(e.target.value)}
                    placeholder="Python, AWS, FastAPI" className="mt-1.5 block w-full input-apple text-xs font-mono" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Nice-to-have skills</label>
                  <input data-testid="nice-input" value={nice} onChange={(e) => setNice(e.target.value)}
                    placeholder="Kafka, Kubernetes" className="mt-1.5 block w-full input-apple text-xs font-mono" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Min years experience</label>
                  <input data-testid="years-input" type="number" min="0" value={minYears} onChange={(e) => setMinYears(e.target.value)}
                    placeholder="5" className="mt-1.5 block w-full input-apple text-xs" />
                </div>
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Shortlist Count</label>
                  <input data-testid="shortlist-input" type="number" min="1" max="50" value={shortlist}
                    onChange={(e) => setShortlist(e.target.value)} className="mt-1.5 block w-full input-apple text-xs" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-sub">Recruiter Notes</label>
                  <textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                    placeholder="Specific factors the AI router should analyze — company stability, culture, deal-breakers..."
                    className="mt-1.5 block w-full textarea-apple text-xs" />
                </div>
              </div>
            </div>
          </section>

          {/* RIGHT: Resume Drag Drop Stack */}
          <section className="lg:col-span-5">
            <div className="card p-8 bg-white sticky top-24 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-ink mb-1">Resume Stack</h2>
                <p className="text-xs text-soft">Drop a batch of PDF, DOCX, or TXT resumes to parse.</p>
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)} 
                onDrop={handleDrop}
                onClick={() => document.getElementById("resume-input").click()}
                data-testid="resume-dropzone"
                className={`border border-dashed hairline p-10 text-center cursor-pointer transition-all rounded-xl ${
                  drag ? "bg-ink text-white" : "bg-haze/30 hover:bg-haze"
                }`}
              >
                <Upload size={20} className="mx-auto mb-3 text-ink" />
                <div className="text-xs font-semibold uppercase tracking-wider text-ink">Drop resumes here</div>
                <div className="text-[10px] text-soft mt-1">or click to browse files</div>
                <input id="resume-input" data-testid="resume-input" type="file" multiple accept=".pdf,.docx,.txt,.md"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (resumes.length + files.length > 50) {
                      toast.error("Maximum 50 resumes are allowed per project.");
                      setResumes((p) => [...p, ...files].slice(0, 50));
                    } else {
                      setResumes((p) => [...p, ...files]);
                    }
                  }} className="hidden" />
              </div>

              {resumes.length > 0 && (
                <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                  {resumes.map((r, i) => (
                    <div key={i} className="flex items-center justify-between border hairline px-4 py-3 text-xs bg-haze2 rounded-xl" data-testid={`resume-item-${i}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={12} className="text-soft shrink-0" />
                        <span className="font-mono text-xs truncate text-ink">{r.name}</span>
                      </div>
                      <button onClick={() => removeResume(i)} className="text-soft hover:text-danger p-1 transition-all"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}

              <div className="text-[10px] font-mono text-soft">{resumes.length} resume(s) loaded</div>

              <div className="pt-4 border-t hairline space-y-2">
                <button
                  onClick={submit} 
                  disabled={busy} 
                  data-testid="create-project-btn"
                  className="w-full btn-accent py-3.5 flex items-center justify-center disabled:opacity-50"
                >
                  <Sparkles size={14} className="mr-2" /> 
                  {busy 
                    ? (uploadProgress ? `Uploading resumes (batch ${uploadProgress.currentChunk}/${uploadProgress.totalChunks})...` : "Creating Project...") 
                    : "Create Project"}
                </button>
                <button 
                  onClick={() => nav("/app")} 
                  className="w-full btn-ghost py-3"
                >
                  Cancel
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
